/**
 * SASHA - Smart Audio System Hosting Advertisements
 * Web Component principale
 */

import { StateManager } from './StateManager.js';
import { AudioManager } from './AudioManager.js';
import { WaveformRenderer } from './WaveformRenderer.js';
import { ConfigManager } from '../managers/ConfigManager.js';
import { ThemeManager } from '../managers/ThemeManager.js';
import { ControlsManager } from '../ui/ControlsManager.js';
import { DOMUpdater } from '../ui/DOMUpdater.js';
import { SkeletonLoader } from '../ui/SkeletonLoader.js';
import { EVENTS } from '../constants/defaults.js';
import { dispatchCustomEvent, waitForEvent, isEventReady } from '../utils/events.js';
import { generateId } from '../utils/format.js';
import { $ } from '../utils/dom.js';

export class SashaPlayer extends HTMLElement {
  constructor() {
    super();
    
    // Config Manager
    this.configManager = new ConfigManager(this);
    this.config = this.configManager.getConfig();
    
    // Shadow DOM
    const shadowMode = this.config.advanced.shadowMode;
    this._shadowRoot = this.attachShadow({ mode: shadowMode });
    
    // State Manager
    this.stateManager = new StateManager();
    
    // Ad Manager e Audio Manager - inizializzati in connectedCallback
    this.adManager = null;
    this.audioManager = null;
    
    // Waveform Renderer
    this.waveformRenderer = null;
    
    // Theme Manager
    this.themeManager = window.SashaThemeManager || new ThemeManager();
    this.currentTheme = this.config.theme || 'default';
    
    // Multi-istanza
    this.instanceId = generateId();
    this.registerInstance();
    
    // Lazy loading state
    this.isLazyLoaded = false;
    this.observer = null;
    
    // Sottoscrizione a cambiamenti stato
    this.setupStateSubscriptions();
    
    this.configReadyPromise = this.prepareConfig();
  }
  
  /**
   * Getter per shadow root (compatibilità mode closed)
   */
  get shadowRoot() {
    return this._shadowRoot || super.shadowRoot;
  }
  
  /**
   * Attributi osservati
   */
  static get observedAttributes() {
    return ['config', 'theme', 'themes-path', 'shadow-mode'];
  }
  
  /**
   * Connected callback
   */
  async connectedCallback() {
    // Render skeleton SEMPRE per evitare CLS
    this.renderSkeleton();
    
    // Assicura che la configurazione (remota) sia pronta prima di procedere
    await this.configReadyPromise;
    
    // Controlla se deve essere lazy loaded
    if (this.config.advanced?.lazyLoad === true) {
      this.setupLazyLoading();
      return;
    }
    
    // Caricamento immediato (ma dopo breve delay per mostrare skeleton)
    // Questo evita CLS anche per player above the fold
    requestAnimationFrame(() => {
      this.initializePlayer();
    });
  }
  
  /**
   * Setup lazy loading con Intersection Observer
   */
  setupLazyLoading() {
    // Skeleton già renderizzato in connectedCallback
    
    if ('IntersectionObserver' in window) {
      const options = {
        root: null,
        rootMargin: '50px', // Carica 50px prima che entri nel viewport
        threshold: 0.1
      };
      
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.isLazyLoaded) {
            this.initializePlayer();
          }
        });
      }, options);
      
      this.observer.observe(this);
    } else {
      // Fallback per browser vecchi - carica immediatamente
      this.initializePlayer();
    }
  }
  
  /**
   * Render skeleton per evitare CLS
   */
  renderSkeleton() {
    const criticalCSS = `
      .sasha-player { 
        display: block; 
        width: 100%; 
        min-height: 180px; 
        background: #1a1a2e; 
        border-radius: 16px;
        overflow: hidden;
      }
      ${SkeletonLoader.getSkeletonCSS()}
    `;
    
    this._shadowRoot.innerHTML = `
      <style>${criticalCSS}</style>
      ${SkeletonLoader.createSkeleton(this.config)}
    `;
  }
  
  /**
   * Inizializza il player completo
   */
  async initializePlayer() {
    if (this.isLazyLoaded) return;
    
    this.isLazyLoaded = true;
    
    // Cleanup observer
    if (this.observer) {
      this.observer.disconnect();
    }
    
    // Assicura che la configurazione sia pronta (remota inclusa)
    await this.configReadyPromise;
    
    // Aggiorna renderer/manager con la config finale
    if (!this.waveformRenderer) {
      this.waveformRenderer = new WaveformRenderer(this.config);
    }
    
    // Aspetta readiness di dipendenze
    if (!isEventReady(EVENTS.THEME_MANAGER_READY)) {
      await waitForEvent(EVENTS.THEME_MANAGER_READY);
    }
    
    // Inizializza Ad Manager se ci sono ads configurate
    if (this.config.ads && this.config.ads.length > 0) {
      const { AdManager } = await import('../managers/AdManager.js');
      this.adManager = new AdManager(this.config.ads, { 
        frequencyCapping: this.config.frequencyCapping,
        stateManager: this.stateManager
      });
    }
    
    // Inizializza Audio Manager (dopo AdManager)
    this.audioManager = new AudioManager(this.config, this.stateManager, this.adManager);
    
    // Carica tema
    await this.loadTheme();
    
    // Render
    await this.render();
    
    // Setup UI
    this.setupUI();
    
    // Dispatch ready event
    dispatchCustomEvent(this, EVENTS.READY, { player: this });
    window.SashaPlayerReady = true;
  }
  
  /**
   * Disconnected callback
   */
  disconnectedCallback() {
    this.cleanup();
  }
  
  /**
   * Attribute changed callback
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    
    if (name === 'theme' && this.isConnected) {
      this.loadTheme(newValue);
    }
  }
  
  /**
   * Carica tema
   */
  async loadTheme(themeName) {
    themeName = themeName || this.config.theme || 'default';
    const themesPath = this.config.themesPath || 'themes/';
    
    this.themeManager.setThemesPath(themesPath);
    
    try {
      // Passa debugMode dal config
      const debugMode = this.config.advanced?.debugMode || false;
      await this.themeManager.loadTheme(themeName, { debugMode });
      this.currentTheme = themeName;
    } catch (e) {
      console.error('❌ Errore caricamento tema:', e);
    }
  }
  
  /**
   * Render componente
   */
  async render() {
    let template;
    
    // Usa template del tema se disponibile
    if (this.themeManager) {
      template = this.themeManager.renderTemplate(this.currentTheme, this.config);
    }
    
    // Fallback a template default
    if (!template) {
      template = this.getDefaultTemplate();
    }
    
    // Pulisci e render
    this.shadowRoot.innerHTML = template;
    
    // Applica tema
    if (this.themeManager && this.currentTheme) {
      this.themeManager.applyTheme(this.shadowRoot, this.currentTheme);
    }
  }
  
  /**
   * Template HTML default
   */
  getDefaultTemplate() {
    return `
      <div class="sasha-container">
        <div class="sasha-cover">
          <img src="${this.config.coverImage}" alt="${this.config.title}" />
          <button class="sasha-btn sasha-btn-play-overlay" id="btn-play" title="Play/Pausa">
            <svg class="play-icon" viewBox="0 0 24 24" width="48" height="48">
              <path d="M8 5v14l11-7z" fill="currentColor"/>
            </svg>
            <svg class="pause-icon" viewBox="0 0 24 24" width="48" height="48">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" fill="currentColor"/>
            </svg>
          </button>
        </div>
        
        <div class="sasha-content">
          <div class="sasha-info">
            <h2 class="sasha-title">${this.config.title}</h2>
            <p class="sasha-subtitle">${this.config.subtitle}</p>
          </div>
          
          ${this.config.sponsorLogo ? `
            <a href="${this.config.sponsorLink}" target="_blank" rel="noopener noreferrer" class="sasha-sponsor">
              <span class="sasha-sponsor-claim">${this.config.sponsorClaim}</span>
              <img src="${this.config.sponsorLogo}" alt="${this.config.sponsorClaim}" class="sasha-sponsor-logo" />
            </a>
          ` : ''}
          
          <div class="sasha-status" id="status"></div>
          
          <div class="sasha-waveform-row">
            <canvas class="sasha-waveform" id="waveform"></canvas>
            <div class="sasha-right-controls">
              <input type="range" min="0" max="100" value="100" class="sasha-volume-slider" id="volume-slider" />
              <button class="sasha-btn sasha-btn-volume" id="btn-mute" title="Mute">
                <svg class="volume-icon" viewBox="0 0 24 24" width="20" height="20">
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" fill="currentColor"/>
                </svg>
                <svg class="mute-icon" viewBox="0 0 24 24" width="20" height="20">
                  <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" fill="currentColor"/>
                </svg>
              </button>
              ${this.config.showShareButton ? `
                <button class="sasha-btn sasha-btn-share" id="btn-share" title="Condividi">
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" fill="currentColor"/>
                  </svg>
                </button>
              ` : ''}
            </div>
          </div>
          
          <div class="sasha-controls-area">
            <div class="sasha-skip-controls">
              <button class="sasha-btn sasha-btn-skip" id="btn-backward" title="Indietro ${this.config.skipSeconds}s">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path d="M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z" fill="currentColor"/>
                </svg>
                <span class="skip-seconds">-${this.config.skipSeconds}s</span>
              </button>
              <button class="sasha-btn sasha-btn-skip" id="btn-forward" title="Avanti ${this.config.skipSeconds}s">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path d="M13 6v12l8.5-6L13 6zM4 18l8.5-6L4 6v12z" fill="currentColor"/>
                </svg>
                <span class="skip-seconds">+${this.config.skipSeconds}s</span>
              </button>
            </div>
            
            <div class="sasha-progress">
              <span class="sasha-time" id="current-time">0:00</span>
              <div class="sasha-progress-bar" id="progress-bar">
                <div class="sasha-progress-fill" id="progress-fill"></div>
              </div>
              <span class="sasha-time" id="duration">0:00</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }
  
  /**
   * Setup UI
   */
  setupUI() {
    // Controls Manager
    this.controlsManager = new ControlsManager(this.shadowRoot, this.audioManager, this.config);
    this.controlsManager.attachEventListeners();
    
    // Passa controlsManager ad AudioManager per gestione disabilitazione controlli durante ads
    this.audioManager.controlsManager = this.controlsManager;
    
    // DOM Updater
    this.domUpdater = new DOMUpdater(this.shadowRoot);
    
    // Sincronizza stato iniziale con DOM
    const currentState = this.stateManager.getState();
    this.domUpdater.updatePlayButton(currentState.isPlaying);
    this.domUpdater.updateLoadingState(currentState.isLoading);
    this.domUpdater.updateMuteState(currentState.isMuted);
    this.domUpdater.updateVolumeSlider(currentState.volume, currentState.isMuted);
    
    // Setup Web Audio API (obbligatorio per GainNode, anche senza waveform)
    const fftSize = this.config.advanced?.fftSize || 256;
    const { analyser, dataArray } = this.audioManager.setupWebAudio(fftSize);
    
    // Setup waveform (opzionale)
    const canvas = $(this.shadowRoot, '#waveform');
    if (canvas) {
      this.waveformRenderer.setupCanvas(canvas);
      this.waveformRenderer.setupAnalyser(analyser, dataArray);
    }

    // Debug: mostra variabili colori effettive
    if (this.config.advanced?.debugColors) {
      // Aspetta che le CSS custom properties siano applicate dal tema
      setTimeout(() => {
        const container = $(this.shadowRoot, '.sasha-container');
        if (container) {
          const s = getComputedStyle(container);
          const accentColor = s.getPropertyValue('--sasha-accent-color').trim() || '(non impostato)';
          const waveColor = s.getPropertyValue('--sasha-waveform-color').trim() || '(non impostato)';
          const waveBgColor = s.getPropertyValue('--sasha-waveform-background-color').trim() || '(non impostato)';
          
          const badge = document.createElement('div');
          badge.className = 'sasha-debug-badge';
          badge.innerHTML = `
            <div style="font-weight:bold;margin-bottom:4px">🔍 Debug Colori</div>
            <div>accent: <code>${accentColor}</code></div>
            <div>wave: <code>${waveColor}</code></div>
            <div>waveBg: <code>${waveBgColor}</code></div>
          `;
          container.appendChild(badge);
          
          const style = document.createElement('style');
          style.textContent = `.sasha-debug-badge{position:absolute;bottom:8px;left:8px;background:rgba(0,0,0,.85);color:#fff;font:11px/1.4 system-ui;padding:8px 10px;border-radius:8px;border:1px solid rgba(255,255,255,.3);z-index:20;max-width:90%} .sasha-debug-badge code{background:rgba(255,255,255,.15);padding:2px 4px;border-radius:3px;font-family:monospace;font-size:10px}`;
          this.shadowRoot.appendChild(style);
          
          console.log('🔍 Debug colori player:', { accentColor, waveColor, waveBgColor });
        }
      }, 100);
    }
    
  }
  
  /**
   * Setup sottoscrizioni a cambiamenti stato
   */
  setupStateSubscriptions() {
    this.stateManager.subscribe((newState, prevState) => {
      if (!this.domUpdater) return;
      
      // Play button
      if (newState.isPlaying !== prevState.isPlaying) {
        this.domUpdater.updatePlayButton(newState.isPlaying);
        
        if (newState.isPlaying) {
          this.waveformRenderer.start();
          this.stopOtherInstances();
        } else {
          this.waveformRenderer.stop();
        }
      }
      
      // Progress
      if (newState.currentTime !== prevState.currentTime) {
        this.domUpdater.updateProgress(newState.currentTime, newState.duration);
      }
      
      // Duration
      if (newState.duration !== prevState.duration) {
        this.domUpdater.updateDuration(newState.duration);
      }
      
      // Loading
      if (newState.isLoading !== prevState.isLoading) {
        this.domUpdater.updateLoadingState(newState.isLoading);
      }
      
      // Mute
      if (newState.isMuted !== prevState.isMuted) {
        this.domUpdater.updateMuteState(newState.isMuted);
        this.domUpdater.updateVolumeSlider(newState.volume, newState.isMuted);
      }
      
      // Volume
      if (newState.volume !== prevState.volume) {
        this.domUpdater.updateVolumeSlider(newState.volume, newState.isMuted);
      }
    });
  }
  
  /**
   * Prepara la configurazione, includendo eventuale fetch remoto e Spreaker.
   * @returns {Promise<Object>}
   */
  async prepareConfig() {
    // Carica config remota se presente
    if (this.configManager.hasRemoteConfig()) {
      try {
        const remoteConfig = await this.configManager.loadRemoteConfig();
        if (remoteConfig) {
          this.config = remoteConfig;
        }
      } catch (error) {
        console.warn('⚠️ Config remota non caricata:', error);
      }
    }
    
    // Carica config Spreaker se presente (dopo remote config per permettere override)
    if (this.configManager.hasSpreakerEpisode()) {
      try {
        const spreakerConfig = await this.configManager.loadSpreakerConfig();
        if (spreakerConfig) {
          this.config = spreakerConfig;
        }
      } catch (error) {
        console.warn('⚠️ Config Spreaker non caricata:', error);
      }
    }
    
    dispatchCustomEvent(this, EVENTS.CONFIG_READY, { config: this.config });
    return this.config;
  }
  
  /**
   * Registra istanza (multi-istanza support)
   */
  registerInstance() {
    if (!window.sashaInstances) {
      window.sashaInstances = new Set();
    }
    window.sashaInstances.add(this);
  }
  
  /**
   * Stop altre istanze
   */
  stopOtherInstances() {
    if (!window.sashaInstances) return;
    
    window.sashaInstances.forEach(instance => {
      if (instance !== this && instance.stateManager.getState().isPlaying) {
        instance.audioManager.pause();
      }
    });
  }
  
  /**
   * Cleanup
   */
  cleanup() {
    // Cleanup observer
    if (this.observer) {
      this.observer.disconnect();
    }
    
    if (this.waveformRenderer) {
      this.waveformRenderer.destroy();
    }
    
    if (this.audioManager) {
      this.audioManager.destroy();
    }
    
    if (this.adManager) {
      this.adManager.destroy();
    }
    
    if (window.sashaInstances) {
      window.sashaInstances.delete(this);
    }
  }
  
  // Public API
  play() {
    return this.audioManager.play();
  }
  
  pause() {
    this.audioManager.pause();
  }
  
  toggle() {
    return this.controlsManager.togglePlay();
  }
  
  skip(seconds) {
    this.audioManager.skip(seconds);
  }
  
  setVolume(value) {
    this.audioManager.setVolume(value);
  }
  
  getState() {
    return this.stateManager.getState();
  }
}

